/**
 * useClvTrends Hook
 * Weekly CLV trends for the CLV view. Shared between web and mobile. Loads on demand
 * (the view calls load when it opens), not on mount.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { ClvTrendPoint } from '../clvTypes';

export interface UseClvTrendsOptions {
  httpClient: HttpClient;
}

export interface UseClvTrendsResult {
  trends: ClvTrendPoint[];
  loading: boolean;
  error: string | null;
  /** Whether a load has finished at least once, so an empty list means no data */
  loaded: boolean;
  load: () => Promise<void>;
}

export function useClvTrends({ httpClient }: UseClvTrendsOptions): UseClvTrendsResult {
  const [trends, setTrends] = useState<ClvTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await httpClient.get<{ trends?: ClvTrendPoint[] }>('/api/ml/clv-history');
      setTrends(data.trends ?? []);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CLV data');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  return { trends, loading, error, loaded, load };
}
